/**
 * SocialPulseTerms — Terms of Service for SocialPulse.com and the network.
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import SocialPulseHeader from "@/components/socialpulse/SocialPulseHeader";
import SocialPulseFooter from "@/components/socialpulse/SocialPulseFooter";

const EFFECTIVE_DATE = "February 11, 2026";

export default function SocialPulseTerms() {
  useEffect(() => {
    document.title = "Terms of Service · SocialPulse";
    setMeta("description", "The terms governing your use of SocialPulse.com and the SocialPulse network of specialist community sites.");
    setLink("canonical", "https://socialpulse.com/terms");
    setMeta("og:title", "Terms of Service · SocialPulse", "property");
    setMeta("og:url", "https://socialpulse.com/terms", "property");
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="sp-terms-page">
      <SocialPulseHeader />
      <main className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-10 py-10 sm:py-14">
        <p className="text-xs font-semibold uppercase tracking-[.22em] text-slate-500">Legal</p>
        <h1
          className="mt-3 font-serif font-bold tracking-tight text-slate-900 leading-tight text-3xl sm:text-4xl"
          data-testid="sp-terms-h1"
        >
          Terms of Service
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Effective {EFFECTIVE_DATE} · Applies to SocialPulse.com and every property in the SocialPulse network.
        </p>

        <div className="prose prose-slate max-w-none mt-8 text-slate-700 text-[15px] leading-relaxed">

          <h2>1. Who we are</h2>
          <p>
            SocialPulse.com is operated by <strong>SocialPulse Inc.</strong>
            (&ldquo;SocialPulse&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;). We
            operate a network of specialist community sites including but not
            limited to CivicPulse.blog, SocialPulse.blog, WorldPulse.pro,
            USAPulse.net, Indias.com, Kerala.com, HotelsPulse.pro,
            PhotoPulse.pro, TheNewsPulse.pro, FoodPulse, FarmersPulse,
            NaturePulse, TravelerPulse, AlumniPulse, WebsiteTech.pro,
            FamilyTree.team and other properties listed in our Network
            Registry (each a &ldquo;Network Site&rdquo;). Together the Network Sites and
            SocialPulse.com are the &ldquo;Services&rdquo;.
          </p>

          <h2>2. Acceptance of these Terms</h2>
          <p>
            By creating an account, accessing, or using the Services, you
            confirm that you have read, understood, and agree to be bound by
            these Terms and our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>. If you do not agree, do not use the Services.
          </p>

          <h2>3. Eligibility</h2>
          <p>
            You must be at least 13 years old (or the minimum age required by
            your jurisdiction, whichever is higher) to create a SocialPulse
            account. Some Network Sites — including memorial, family tree, or
            health-related surfaces — may set higher age or verification
            thresholds. Users under 18 may only use the Services with the
            consent and supervision of a parent or legal guardian.
          </p>

          <h2>4. Your account and one-identity model</h2>
          <p>
            SocialPulse operates a single shared identity across every Network
            Site. When you sign in on any Network Site with SocialPulse SSO
            you may be automatically recognised on every other Network Site
            you visit. You are responsible for maintaining the confidentiality
            of your credentials and for all activity under your account. Tell
            us immediately at <a href="mailto:support@socialpulse.com" className="text-blue-600 hover:underline">support@socialpulse.com</a> if you suspect unauthorised access.
          </p>

          <h2>5. Content you post</h2>
          <p>
            You retain ownership of the content you submit ("Your Content") —
            posts, comments, photos, civic reports, articles, reviews, family
            records, memorials and anything else. By posting Your Content to
            the Services you grant SocialPulse a worldwide, non-exclusive,
            royalty-free licence to host, store, reproduce, translate, modify
            for formatting, publish, and display Your Content across the
            Network Sites solely for the purpose of operating and improving
            the Services and delivering your content to the audiences you
            select. You warrant that you have all rights necessary to grant
            this licence.
          </p>

          <h2>6. Community rules</h2>
          <p>
            You must not use the Services to post or transmit content that:
            (a) is unlawful, defamatory, harassing, abusive, or hateful;
            (b) infringes any intellectual-property, privacy, or publicity right;
            (c) impersonates any person or entity;
            (d) contains malware, spam, or unauthorised advertising;
            (e) exploits minors, or promotes violence or self-harm;
            (f) misrepresents the source, location, or verification status
                of civic reports, medical needs, memorials, family records,
                or organisations.
          </p>
          <p>
            We may remove content, suspend or terminate accounts, and cooperate
            with law-enforcement when these rules are broken.
          </p>

          <h2>7. Verification labels and Social Capital</h2>
          <p>
            SocialPulse assigns verification labels (e.g. Verified Authority,
            Verified Organisation, Trusted Reporter) based on evidence we
            collect at the time of verification. Labels reflect our
            best-effort assessment; they are not a guarantee of accuracy,
            quality, or safety of any content, organisation, or third party.
            Social Capital scores reflect community contributions recorded on
            your account ledger and can be disputed via our correction
            procedure.
          </p>

          <h2>8. Third-party content and Network Sites</h2>
          <p>
            The Services surface content that originates on Network Sites and
            in some cases on independent third-party sites. Each item shows
            the canonical owner and a link to the source. SocialPulse Inc.
            operates each Network Site listed in the Network Registry, but
            individual editorial, moderation, and pricing decisions may
            differ per site. Third-party links are provided for convenience
            and their content is not our responsibility.
          </p>

          <h2>9. Payments and paid features</h2>
          <p>
            Certain Network Sites (for example, HotelsPulse.pro bookings or
            publisher subscription products) may charge fees. Pricing, refund,
            and cancellation terms specific to those features are disclosed
            at the point of purchase and form part of these Terms.
          </p>

          <h2>10. Prohibited use of automated tools</h2>
          <p>
            You must not scrape, spider, crawl, or otherwise programmatically
            harvest the Services except through our published Federation API
            with a valid Consumer ID and within the applicable rate limits.
            You must not attempt to bypass authentication, rate-limiting, or
            content-verification systems.
          </p>

          <h2>11. Intellectual property</h2>
          <p>
            The SocialPulse name, logos, and other proprietary marks used on
            the Services belong to SocialPulse Inc. Except for Your Content
            and third-party content displayed with attribution, all material
            on the Services (software, design, text, illustrations, database
            structure) is owned by SocialPulse or its licensors and is
            protected by intellectual-property law.
          </p>

          <h2>12. Disclaimers</h2>
          <p>
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT
            THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ANY
            CONTENT, VERIFICATION, LOCATION, OR OUTCOME IS ACCURATE OR
            RELIABLE. SOCIALPULSE IS NOT AN EMERGENCY SERVICE — IN AN
            EMERGENCY, CONTACT LOCAL AUTHORITIES.
          </p>

          <h2>13. Limitation of liability</h2>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, SOCIALPULSE INC.'S TOTAL
            AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO YOUR USE OF THE
            SERVICES IS LIMITED TO THE GREATER OF (a) THE AMOUNT YOU PAID US
            IN THE PRECEDING TWELVE MONTHS, OR (b) US$100. WE ARE NOT LIABLE
            FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
          </p>

          <h2>14. Termination</h2>
          <p>
            You may close your account at any time via account settings on
            SocialPulse.com. We may suspend or terminate your access to any
            or all Network Sites at our discretion for violation of these
            Terms, security concerns, or as required by law. Deletion is
            handled per our Privacy Policy and applicable data-protection law.
          </p>

          <h2>15. Governing law and disputes</h2>
          <p>
            These Terms are governed by the laws of the jurisdiction in which
            SocialPulse Inc. is incorporated, without regard to conflict-of-laws
            principles. Any dispute will be resolved in the competent courts of
            that jurisdiction unless mandatory local law provides otherwise.
          </p>

          <h2>16. Changes to these Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will
            be highlighted at the top of this page and, where required by law,
            notified to you by email. Continued use of the Services after the
            change means you accept the revised Terms.
          </p>

          <h2>17. Contact</h2>
          <p>
            Questions about these Terms should be sent to <a href="mailto:support@socialpulse.com" className="text-blue-600 hover:underline">support@socialpulse.com</a>. For legal or copyright notices, write to
            <a href="mailto:legal@socialpulse.com" className="ml-1 text-blue-600 hover:underline">legal@socialpulse.com</a>.
          </p>
        </div>
      </main>
      <SocialPulseFooter />
    </div>
  );
}

function setMeta(name, content, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}
function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}
